import { CoverageStatus } from "../../../../enums/coverageStatus";
import type { InactiveInsuranceProps } from "../inactiveInsurance/inactiveInsurance";

/**
 * Config for the EV statuses that render a manual-entry message instead of financials.
 *
 * @property variant - `InactiveInsurance` message variant to show.
 * @property showUpdateManually - Whether this status uses the "Update Manually" flow.
 *   When true, the manual editor opens via the `showManualCoverageEditor` shell flag
 *   (ERROR / EV_TYPE_NOT_SUPPORTED) and the plan-summary Edit control is suppressed;
 *   when false, edit mode is entered via the plan-summary Edit control (IN_PROGRESS).
 *   This single flag drives the button, the editor-open check, and `planSummaryEnableEdit`.
 */
export interface ManualEntryStatusConfig {
  variant: NonNullable<InactiveInsuranceProps["variant"]>;
  showUpdateManually: boolean;
}

/**
 * Single source of truth for EV statuses that swap financials for a manual-entry
 * message. Both the unified content component (rendering) and
 * {@link useEvCoverageEditOrchestration} (edit state machine) derive from this map,
 * so adding a status here wires up both halves consistently.
 */
export const MANUAL_ENTRY_STATUS_CONFIG: Partial<
  Record<CoverageStatus, ManualEntryStatusConfig>
> = {
  [CoverageStatus.ERROR]: {
    variant: "unableToRetrieve",
    showUpdateManually: true,
  },
  [CoverageStatus.IN_PROGRESS]: {
    variant: "fetching",
    showUpdateManually: false,
  },
  [CoverageStatus.EV_TYPE_NOT_SUPPORTED]: {
    variant: "notSupported",
    showUpdateManually: true,
  },
};

/**
 * Whether the status uses the "Update Manually" flow (manual BV entry via the full
 * manual-editor shell) rather than the plan-summary Edit control. Callers that gate
 * edit UI must also AND with `!isManuallyUpdated` so a previously saved manual fix
 * can re-enter edit via plan-summary Edit (matching `renderMain`).
 *
 * @input coverageStatus: Current EV coverage retrieval status.
 * @return boolean: True for statuses whose editor opens via the manual-editor shell.
 */
export function isManualBvEntryStatus(
  coverageStatus: CoverageStatus | undefined,
): boolean {
  return (
    !!coverageStatus &&
    !!MANUAL_ENTRY_STATUS_CONFIG[coverageStatus]?.showUpdateManually
  );
}
